import * as React from 'react';
import { useEffect } from 'react';

import { formatDateAsUTC, formatDateTimeWithUserTimezone, formatDateWithUserTimezone, formatTimeWithUserTimezone } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';

import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useUserStore } from '@/stores/user.store';

import CalendarBlankIcon from '../icons/calendar-blank';
import { CloseSmallIcon } from '../icons/close-small';

interface DatePickerProps {
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  disabledDays?: (date: Date) => boolean;
  showTimePicker?: boolean;
  showDatePicker?: boolean;
  error?: boolean;
  disabled?: boolean;
  useUtc?: boolean;
  isClearable?: boolean;
}

export function DatePicker({
  error,
  date,
  setDate,
  placeholder,
  className,
  disabledDays,
  showTimePicker,
  showDatePicker = true,
  disabled,
  useUtc,
  isClearable,
}: DatePickerProps) {
  const userTimezone = useUserStore.getState().user?.timezone;
  const [isOpen, setIsOpen] = React.useState(false);
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);

  useEffect(() => {
    if (showTimePicker && !showDatePicker && !date) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      setDate(now);
    }
  }, [date, setDate, showTimePicker, showDatePicker]);

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      if (useUtc) {
        // Create UTC midnight date
        const utcDate = new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()));
        setDate(utcDate);
      } else {
        // Preserve the time if there was a previous date — coerce, since callers may pass an ISO string rather than a Date.
        if (date) {
          const previousDate = new Date(date);
          if (!Number.isNaN(previousDate.getTime())) {
            selectedDate.setHours(previousDate.getHours());
            selectedDate.setMinutes(previousDate.getMinutes());
          }
        }
        setDate(selectedDate);
      }
      // Close the popover if we're only showing the date picker or if we don't have time picker
      if (!showTimePicker) {
        setIsOpen(false);
      }
    }
  };

  const handleTimeChange = (type: 'hour' | 'minute' | 'ampm', value: string) => {
    const currentDate = date || new Date();
    const newDate = new Date(currentDate);

    if (type === 'hour') {
      newDate.setHours((parseInt(value) % 12) + (newDate.getHours() >= 12 ? 12 : 0));
    } else if (type === 'minute') {
      newDate.setMinutes(parseInt(value));
    } else if (type === 'ampm') {
      const currentHours = newDate.getHours();
      newDate.setHours(value === 'PM' ? (currentHours % 12) + 12 : currentHours % 12);
      // Close the popover when AM/PM is selected
      setIsOpen(false);
    }
    setDate(newDate);
  };

  const displayValue = React.useMemo(() => {
    if (!date) return '';

    if (showTimePicker && !showDatePicker) {
      return formatTimeWithUserTimezone(date, userTimezone);
    } else if (showTimePicker && showDatePicker) {
      return formatDateTimeWithUserTimezone(date, userTimezone);
    } else {
      return useUtc ? formatDateAsUTC(date, userTimezone) : formatDateWithUserTimezone(date, userTimezone);
    }
  }, [date, showTimePicker, showDatePicker, useUtc, userTimezone]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger className={cn(className)} asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'border-grey-3 flex h-10 w-full justify-between gap-2 border-2 p-2 hover:bg-white',
            !displayValue && 'text-grey-5',
            isOpen && 'border-default-blue',
            error && 'border-default-red',
            disabled && 'bg-grey-1 hover:bg-grey-1 cursor-not-allowed disabled:pointer-events-auto disabled:opacity-100',
            disabled && displayValue && 'text-default-text',
          )}>
          {displayValue || <span>{placeholder}</span>}
          <div className="flex items-center gap-1">
            {isClearable && date && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setDate(undefined);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    setDate(undefined);
                  }
                }}
                aria-label="Clear date"
                className="text-default-text">
                <CloseSmallIcon />
              </span>
            )}
            <CalendarBlankIcon className="text-default-text size-4" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <div className="sm:flex">
          {showDatePicker && (
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateSelect}
              disabled={disabledDays}
              initialFocus
              captionLayout="dropdown"
              startMonth={new Date(1900, 0, 1)}
              endMonth={new Date(2100, 11, 31)}
              defaultMonth={date || new Date()}
            />
          )}
          {showTimePicker && (
            <div className="flex flex-col divide-y sm:h-[300px] sm:flex-row sm:divide-x sm:divide-y-0">
              <ScrollArea className="w-64 sm:w-auto">
                <div className="flex p-2 sm:flex-col">
                  {hours.reverse().map((hour) => (
                    <Button
                      key={hour}
                      size="icon"
                      variant={date && date.getHours() % 12 === hour % 12 ? 'default' : 'ghost'}
                      className="aspect-square shrink-0 sm:w-full"
                      onClick={() => handleTimeChange('hour', hour.toString())}>
                      {hour}
                    </Button>
                  ))}
                </div>
                <ScrollBar orientation="horizontal" className="sm:hidden" />
              </ScrollArea>
              <ScrollArea className="w-64 sm:w-auto">
                <div className="flex p-2 sm:flex-col">
                  {Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => (
                    <Button
                      key={minute}
                      size="icon"
                      variant={date && date.getMinutes() === minute ? 'default' : 'ghost'}
                      className="aspect-square shrink-0 sm:w-full"
                      onClick={() => handleTimeChange('minute', minute.toString())}>
                      {minute.toString().padStart(2, '0')}
                    </Button>
                  ))}
                </div>
                <ScrollBar orientation="horizontal" className="sm:hidden" />
              </ScrollArea>
              <ScrollArea>
                <div className="flex p-2 sm:flex-col">
                  {['AM', 'PM'].map((ampm) => (
                    <Button
                      key={ampm}
                      size="icon"
                      variant={date && ((ampm === 'AM' && date.getHours() < 12) || (ampm === 'PM' && date.getHours() >= 12)) ? 'default' : 'ghost'}
                      className="aspect-square shrink-0 sm:w-full"
                      onClick={() => handleTimeChange('ampm', ampm)}>
                      {ampm}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
